// Libs
import React, { Component } from 'react';
import moment from 'moment';
import 'moment/locale/en-gb';

// Helpers
import TextHelpers from 'helpers/TextHelpers';

//-------------------------------------------------------------------------------------------------------------------

class SuperTable extends Component {

    constructor(props) {
        super(props);
        this.state = {
        };
    }

    componentDidMount() {
        this.buildGroupings(this.props.rows);
    }

    componentDidUpdate(oldProps) {
        let buildGroupings = false;
        if (this.props.rows.length != oldProps.rows.length) {
            buildGroupings = true;
        } else {
            for (var i = 0; i < oldProps.rows.length; i++) {
                const oldRow = oldProps.rows[i];
                const newRow = this.props.rows[i];
                if (oldRow._grouping != newRow._grouping) {
                    buildGroupings = true;
                    break;
                }
            }
        }
        if (buildGroupings) {
            this.buildGroupings(this.props.rows);
        }
    }

    buildGroupings(rows) {
        const groupings = {};
        rows.forEach(row => {
            if (row._grouping) {
                if (!groupings[row._grouping]) {
                    groupings[row._grouping] = {
                        isOpen: false,
                        rows: []
                    };
                }
            }
        });
        this.setState({
            groupings
        });
    }

    toggleGrouping(key) {
        const groupings = { ...this.state.groupings };
        const grouping = groupings[key];
        if (grouping) {
            grouping.isOpen = !grouping.isOpen;
            this.setState({
                groupings
            });
        }
    }

    formatValue(value, type, col) {
        switch ((type || '').toLowerCase()) {
            case 'currency':
                if (value) {
                    return TextHelpers.formatCurrency(value);
                }
            case 'date':
                if (value) {
                    return moment(value).format('DD/MM/YYYY');
                }
                break;
            case 'datetime':
                if (value) {
                    return moment(value).format('DD/MM/YYYY HH:mm');
                }
                break;
            case 'number':
                if (value) {
                    return value;
                } else {
                    return (col && col.blankIfZero ? '' : '0');
                }
            case 'percentage':
                if (value) {
                    return Math.round(Number(value) * 10000) / 100 + '%';
                } else {
                    return (col && col.blankIfZero ? '' : '0%');
                }
            case 'duration':
                if (value) {
                    return TextHelpers.formatDuration(Number(value));
                }
                break;
            case 'boolean':
                if (value !== undefined) {
                    return value === true ? "Yes" : "No";
                }
                break;
            default:
                return value;
        }
        return '';
    }

    // NEW: compute rowSpan runs for columns where consolidateRows is truthy
    computeRowSpans(visibleRows, colArray) {
        const map = {}; // { colName: number[] } values: >1 => rowSpan; 1 => normal; 0 => hide
        const consolidatedCols = colArray.filter(ci => ci.col && ci.col.consolidateRows);
        consolidatedCols.forEach(ci => {
            const name = ci.name;
            map[name] = new Array(visibleRows.length).fill(1);
            let i = 0;
            while (i < visibleRows.length) {
                const base = visibleRows[i].row[name];
                let j = i + 1;
                while (j < visibleRows.length && visibleRows[j].row[name] === base) {
                    j++;
                }
                const span = j - i;
                if (span > 1) {
                    map[name][i] = span; // apply rowSpan to the first
                    for (let k = i + 1; k < j; k++) map[name][k] = 0; // hide following cells in run
                }
                i = j;
            }
        });
        return map;
    }

    render() {
        let {
            className,
            rows,
            cols,
            keyAccessor,
            sorter,
            onClick,
            rowPropsAccessor,
            cellPropsAccessor,
            emptyText,
            sortBy,
            sortDesc,
            hideHeader
        } = this.props;
        const {
            groupings
        } = this.state;

        if (!rows) {
            rows = [];
            console.warn('No rows specified for <SuperTable>');
        }
        if (!cols) {
            cols = {};
            console.warn('No cols specified for <SuperTable>');
        }

        if (sorter) {
            rows.sort(sorter);
        } else if (sortBy) {
            if (cols[sortBy].sort) {
                rows.sort(cols[sortBy].sort);
                if (sortDesc) {
                    rows.reverse();
                }
            } else {
                let getDiff;
                switch (cols[sortBy].type) {
                    case 'date':
                        getDiff = (a, b) => {
                            a = a[sortBy];
                            b = b[sortBy];
                            const diff = moment.duration(moment(b).diff(moment(a))).asMinutes();
                            return Math.sign(diff);
                        }
                        break;
                    default:
                        getDiff = (a, b) => {
                            a = a[sortBy];
                            b = b[sortBy];
                            if (a > b) return 1;
                            else if (a < b) return -1;
                            else return 0;
                        };
                        break;
                }
                rows.sort((a, b) => getDiff(a, b) * (sortDesc ? -1 : 1));
            }
        }

        // Convert cols to an array
        const colArray = [];
        let index = 0;
        for (var name in cols) {
            if (cols.hasOwnProperty(name)) {
                const col = cols[name];
                if (col.hide) continue;
                colArray.push({
                    name: name,
                    sortOrder: col.sortOrder || index,
                    col: col,
                    index: index++
                });
            }
        }
        colArray.sort((a, b) => a.sortOrder - b.sortOrder);

        if (onClick) {
            className = (className || '') + ' clickable';
        }

        if (rows.length == 0) {
            return (
                <div className="empty-text">
                    {emptyText}
                </div>
            );
        }

        // If groupings not build yet, skip render - groupings will be built and then render will be called again
        if (!groupings) {
            return null;
        }

        // First pass: build grouping headers and collect visible data rows
        const headerResults = [];
        const groupingsRendered = {};
        const visibleRows = [];
        rows.forEach((row, index) => {
            // Render grouping header
            if (row._grouping && groupings[row._grouping]) {
                if (!groupingsRendered[row._grouping]) {
                    groupingsRendered[row._grouping] = true;
                    headerResults.push(
                        <tr
                            key={'grouping-' + row._grouping}
                            className="grouping-row"
                            onClick={e => this.toggleGrouping(row._grouping)}
                        >
                            <td colSpan={colArray.length}>
                                {groupings[row._grouping].isOpen ?
                                    <span className="fa fa-minus-circle" /> :
                                    <span className="fa fa-plus-circle" />
                                }
                                {row._grouping}
                            </td>
                        </tr>
                    );
                }
                // keep group membership up-to-date
                if (!groupings[row._grouping].rows.includes(row)) {
                    groupings[row._grouping].rows.push(row);
                }
            }

            // Only include if grouping is open (or no grouping)
            if (!row._grouping || (groupings[row._grouping] && groupings[row._grouping].isOpen)) {
                const id = String((keyAccessor ? keyAccessor(row, index) : null) || index);
                visibleRows.push({ row, id, originalIndex: index });
            }
        });

        // Compute rowSpan consolidation for columns that request it
        const rowSpanMap = this.computeRowSpans(visibleRows, colArray);

        // Second pass: render visible rows with potential rowSpans
        const dataResults = visibleRows.map((vr, vIndex) => {
            const { row, id } = vr;
            const rowProps = {
                onClick: e => onClick ? onClick(row, e) : null,
                ...(rowPropsAccessor ? rowPropsAccessor(row, vIndex) : null)
            };

            return (
                <tr key={keyAccessor ? keyAccessor(row, vIndex) : vIndex} onClick={e => onClick ? onClick(row, e, vIndex) : null} {...(rowPropsAccessor ? rowPropsAccessor(row, vIndex) : null)}>
                    {colArray.map((colInfo, colIndex) => {
                        const shouldRenderCell = this.props.shouldRenderCell ? this.props.shouldRenderCell(colInfo, row, vIndex) : true;
                        if (!shouldRenderCell) return null;

                        // Determine rowSpan / hidden for consolidated columns
                        let rowSpan;
                        let hidden = false;
                        if (colInfo.col && colInfo.col.consolidateRows) {
                            const spans = rowSpanMap[colInfo.name] || [];
                            const val = spans[vIndex];
                            if (val === 0) {
                                hidden = true; // skip rendering this td completely
                            } else if (val > 1) {
                                rowSpan = val;
                            }
                        }

                        if (hidden) return null;

                        const formattedValue = this.formatValue(row[colInfo.name], colInfo.col.type, colInfo.col);
                        const cellKey = `${colInfo.name}-${id}`; // <-- stable per row+column

                        // default TD factory with possible rowSpan + key
                        const defaultCell = (ci, r) => (
                            <td
                                key={cellKey}
                                className={ci.col.className || ''}
                                rowSpan={rowSpan}
                                {...(cellPropsAccessor ? cellPropsAccessor(ci, r, vIndex, colIndex) : null)}
                            >
                                {ci.col.getValue ? ci.col.getValue(ci, r, formattedValue, vIndex) : formattedValue}
                            </td>
                        );

                        if (colInfo.col.renderCell) {
                            const el = colInfo.col.renderCell(colInfo, row);

                            // If custom render provided a TD/element, inject key and rowSpan
                            if (React.isValidElement(el)) {
                                const extraProps = { key: cellKey };
                                if (rowSpan) extraProps.rowSpan = rowSpan;
                                return React.cloneElement(el, { ...extraProps, ...el.props });
                            }

                            // If custom render returned an array, make sure each has a key
                            if (Array.isArray(el)) {
                                return el.map((child, i) => {
                                    if (React.isValidElement(child)) {
                                        const extraProps = { key: `${cellKey}-${i}` };
                                        if (rowSpan && i === 0) extraProps.rowSpan = rowSpan; // only first cell should span
                                        return React.cloneElement(child, { ...extraProps, ...child.props });
                                    }
                                    return <React.Fragment key={`${cellKey}-${i}`}>{child}</React.Fragment>;
                                });
                            }

                            // Primitive/strings: wrap with TD
                            return defaultCell(colInfo, row);
                        }

                        return defaultCell(colInfo, row);
                    })}
                </tr>
            );
        });

        const results = [...headerResults, ...dataResults];

        return (<>
            <div className="table-container">
                <table className={'super-table ' + (className || '')}>
                    {!hideHeader &&
                        <thead>
                            <tr>
                                {colArray.map(colInfo =>
                                    <React.Fragment key={colInfo.name}>
                                        {(colInfo.col.renderHeaderCell || this.props.renderHeaderCell)(colInfo, this.props)}
                                    </React.Fragment>
                                )}
                            </tr>
                        </thead>
                    }
                    <tbody>
                        {results}
                    </tbody>
                </table>
            </div>
        </>);
    }
}

SuperTable.defaultProps = {
    renderHeaderCell: (colInfo, props) => {
        const classNames = [colInfo.col.className || ''];
        if (colInfo.col.sortable) {
            classNames.push('sortable');
            if (colInfo.name == props.sortBy) {
                classNames.push('sort-by');
                if (props.sortDesc) {
                    classNames.push('desc');
                }
            }
        }
        return (
            <th className={classNames.join(' ')} onClick={e => {
                if (colInfo.col.sortable) {
                    const sortDesc = (colInfo.name == props.sortBy ? !props.sortDesc : false);
                    props.onChangeSort(colInfo.name, sortDesc);
                }
            }}>
                {colInfo.col.label}

                {colInfo.name == props.sortBy && (
                    props.sortDesc ?
                        <span className="fa fa-sort-down"></span> :
                        <span className="fa fa-sort-up"></span>
                )}
            </th>
        );
    }
};

export default SuperTable;
